// src\components\common\MobileResourceList.jsx
import React from 'react';
import { Clock, Activity, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Composant de liste des ressources optimisé pour les appareils mobiles
 */
const MobileResourceList = ({ resources, batches }) => {
  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle size={18} className="text-green-500" />;
      case 'in-progress':
        return <Activity size={18} className="text-blue-500" />;
      case 'maintenance':
        return <AlertTriangle size={18} className="text-yellow-500" />;
      case 'error':
        return <AlertTriangle size={18} className="text-red-500" />;
      default:
        return null;
    }
  };

  // Fonction pour formater une date au format fr-FR
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${day}/${month}`;
  };

  // Associer les fournées aux ressources
  const resourcesWithBatches = resources.map(resource => {
    // Trouver toutes les fournées pour cette ressource
    const resourceBatches = batches
      .filter(batch => batch.resourceId === resource.id)
      .sort((a, b) => {
        // Trier par date et heure de début
        const dateA = a.date.split('/').reverse().join('');
        const dateB = b.date.split('/').reverse().join('');
        
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return a.startTime.localeCompare(b.startTime);
      });
    
    // Déterminer si la ressource est en cours d'utilisation
    const currentBatch = resourceBatches.find(batch => batch.status === 'in-progress');
    const upcomingBatches = resourceBatches.filter(batch => batch.status === 'scheduled').slice(0, 2);
    
    return {
      ...resource,
      currentBatch,
      upcomingBatches
    };
  });

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-3 border-b flex items-center">
        <Activity size={18} className="mr-2 text-blue-600" />
        <h2 className="font-semibold">Moyens chauds</h2>
      </div>
      
      <div className="divide-y">
        {resourcesWithBatches.map(resource => {
          // Déterminer le statut effectif
          const effectiveStatus = resource.currentBatch ? 'in-progress' : resource.status;
          
          return (
            <div key={resource.id} className="p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    effectiveStatus === 'available' ? 'bg-green-500' : 
                    effectiveStatus === 'in-progress' ? 'bg-blue-500' : 
                    effectiveStatus === 'maintenance' ? 'bg-yellow-500' : 
                    'bg-red-500'
                  }`}></div>
                  <h3 className="font-bold">{resource.name}</h3>
                </div>
                <div className={`text-xs px-2 py-1 rounded-full ${getStatusColor(effectiveStatus)}`}>
                  {effectiveStatus === 'available' ? 'Disponible' : 
                   effectiveStatus === 'in-progress' ? 'En cours' : 
                   effectiveStatus === 'maintenance' ? 'Maintenance' : 
                   'Erreur'}
                </div>
              </div>
              
              {/* Fournée en cours */}
              {resource.currentBatch && (
                <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center text-blue-800">
                    <Activity size={16} className="mr-1" />
                    <span className="font-medium">En cours:</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <div>
                      <span className="font-medium">{resource.currentBatch.batchNumber}</span>
                      <span className="ml-2">{resource.currentBatch.cycleName}</span>
                    </div>
                    <div className="text-sm">
                      <Clock size={14} className="inline mr-1" />
                      {resource.currentBatch.startTime}-{resource.currentBatch.endTime}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Prochaines fournées */}
              {resource.upcomingBatches.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mt-2 mb-1">Prochaines fournées:</div>
                  {resource.upcomingBatches.map(batch => (
                    <div key={batch.batchNumber} className="flex justify-between items-center text-sm py-1 border-b border-gray-100 last:border-0">
                      <div>
                        <span className="text-gray-700">{batch.batchNumber}</span>
                        <span className="ml-2 text-gray-600">{batch.cycleName}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(batch.date)} {batch.startTime}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Aucune fournée prévue */}
              {!resource.currentBatch && resource.upcomingBatches.length === 0 && (
                <div className="mt-2 text-sm text-gray-500 italic">
                  Aucune fournée programmée
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileResourceList;